import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Stack,
  InputAdornment,
  Pagination,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  Avatar,
  SelectChangeEvent
} from '@mui/material';
import {
  Search,
  LocationOn,
  CalendarToday,
  Work,
  FilterList,
  Business
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { useTenant } from '../../context/TenantContext';

interface Job {
  id: string;
  title: string;
  description: string;
  location: string;
  is_remote: boolean;
  salary_min: number | null;
  salary_max: number | null;
  salary_currency: string;
  requirements: string[];
  benefits: string[];
  expiry_date: string;
  status: string;
  entity_id: string;
  created_at: string;
  views_count: number;
  applications_count: number;
  entity?: {
    name: string;
    logo?: string;
  };
}

export const JobsPage: React.FC = () => {
  const theme = useTheme();
  const { currentTenant, isLoading: tenantLoading, error: tenantError } = useTenant();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [remoteFilter, setRemoteFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const jobsPerPage = 9;

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!currentTenant) {
        setError('No tenant specified. Please use ?tenant=yourcompany in the URL');
        setLoading(false);
        return;
      }

      console.log('🔍 Fetching jobs for tenant:', currentTenant.name, 'ID:', currentTenant.id);

      // Fetch active, public, non-expired jobs FILTERED BY ENTITY
      const { data, error: fetchError } = await supabase
        .from('jobs')
        .select(`
          *,
          entity:entities(name, logo)
        `)
        .eq('entity_id', currentTenant.id)  // ← Filter by current tenant!
        .eq('is_public', true)
        .eq('status', 'active')
        .is('deleted_at', null)
        .gte('expiry_date', new Date().toISOString().split('T')[0])
        .order('created_at', { ascending: false });

      if (fetchError) {
        throw fetchError;
      }

      console.log('✅ Found', data?.length || 0, 'jobs for tenant:', currentTenant.name);
      setJobs(data || []);
    } catch (err: any) {
      console.error('Error fetching jobs:', err);
      setError('Failed to load jobs. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [currentTenant]);

  useEffect(() => {
    if (!tenantLoading && currentTenant) {
      fetchJobs();
    }
  }, [currentTenant, tenantLoading, fetchJobs]);

  // Filter and search logic
  const filteredJobs = jobs.filter(job => {
    const matchesSearch =
      job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.requirements.some(req => req.toLowerCase().includes(searchTerm.toLowerCase())) ||
      job.entity?.name.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesLocation = !locationFilter ||
      job.location.toLowerCase().includes(locationFilter.toLowerCase());

    const matchesRemote =
      remoteFilter === 'all' ||
      (remoteFilter === 'remote' && job.is_remote) ||
      (remoteFilter === 'onsite' && !job.is_remote);

    return matchesSearch && matchesLocation && matchesRemote;
  });

  // Sort logic
  const sortedJobs = [...filteredJobs].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      case 'oldest':
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      case 'salary-high':
        return (b.salary_max || 0) - (a.salary_max || 0);
      case 'salary-low':
        return (a.salary_min || 0) - (b.salary_min || 0);
      case 'title':
        return a.title.localeCompare(b.title);
      case 'company':
        return (a.entity?.name || '').localeCompare(b.entity?.name || '');
      default:
        return 0;
    }
  });

  // Pagination
  const totalPages = Math.ceil(sortedJobs.length / jobsPerPage);
  const startIndex = (currentPage - 1) * jobsPerPage;
  const paginatedJobs = sortedJobs.slice(startIndex, startIndex + jobsPerPage);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatSalary = (min: number | null, max: number | null, currency: string) => {
    if (!min || !max) return 'Competitive';
    return `${currency} ${min.toLocaleString()} - ${max.toLocaleString()}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'Expired';
    if (diffDays === 0) return 'Expires today';
    if (diffDays === 1) return 'Expires tomorrow';
    if (diffDays <= 7) return `Expires in ${diffDays} days`;
    return `Expires ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  };

  if (tenantLoading || loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', gap: 2 }}>
        <CircularProgress size={50} />
        <Typography variant="body1" color="text.secondary">
          {tenantLoading ? 'Loading tenant information...' : 'Loading jobs...'}
        </Typography>
      </Box>
    );
  }

  if (tenantError || error) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error" onClose={() => setError(null)}>
          {tenantError || error}
        </Alert>
        {!currentTenant && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Please add ?tenant=yourcompany to the URL to view jobs for a specific company.
          </Alert>
        )}
        <Button
          variant="contained"
          onClick={fetchJobs}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Modern Search and Filters */}
      <Card elevation={0} sx={{
        mb: 4,
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        background: alpha(theme.palette.background.paper, 0.7),
        backdropFilter: 'blur(10px)'
      }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search jobs, skills, or companies"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.3s',
                    '&:hover': {
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
                    },
                    '&.Mui-focused': {
                      boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.15)}`,
                    }
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Location"
                value={locationFilter}
                onChange={(e) => {
                  setLocationFilter(e.target.value);
                  setCurrentPage(1);
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.3s',
                    '&:hover': {
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
                    },
                    '&.Mui-focused': {
                      boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.15)}`,
                    }
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOn sx={{ color: theme.palette.secondary.main }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth>
                <Select
                  value={remoteFilter}
                  onChange={(e: SelectChangeEvent) => {
                    setRemoteFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  displayEmpty
                  sx={{
                    borderRadius: 2,
                    transition: 'all 0.3s',
                    '&:hover': {
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
                    }
                  }}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="remote">Remote</MenuItem>
                  <MenuItem value="onsite">On-site</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth>
                <Select
                  value={sortBy}
                  onChange={(e: SelectChangeEvent) => setSortBy(e.target.value)}
                  displayEmpty
                  sx={{
                    borderRadius: 2,
                    transition: 'all 0.3s',
                    '&:hover': {
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
                    }
                  }}
                >
                  <MenuItem value="newest">Newest First</MenuItem>
                  <MenuItem value="oldest">Oldest First</MenuItem>
                  <MenuItem value="salary-high">Highest Salary</MenuItem>
                  <MenuItem value="salary-low">Lowest Salary</MenuItem>
                  <MenuItem value="title">Alphabetical</MenuItem>
                  <MenuItem value="company">By Company</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Results Summary */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body1" color="text.secondary">
          {sortedJobs.length === 0
            ? 'No jobs found'
            : `Showing ${startIndex + 1}-${Math.min(startIndex + jobsPerPage, sortedJobs.length)} of ${sortedJobs.length} jobs`
          }
        </Typography>
      </Box>

      {/* Modern Job Cards */}
      <Grid container spacing={3}>
        {paginatedJobs.map((job) => (
          <Grid item xs={12} md={4} key={job.id}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                borderRadius: 3,
                position: 'relative',
                overflow: 'hidden',
                background: theme.palette.background.paper,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  transform: 'scaleX(0)',
                  transformOrigin: 'left',
                  transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                },
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  '&::before': {
                    transform: 'scaleX(1)',
                  }
                }
              }}
            >
              <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Company Info */}
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  {job.entity?.logo ? (
                    <Avatar
                      src={job.entity.logo}
                      alt={job.entity.name}
                      sx={{ width: 48, height: 48 }}
                      variant="rounded"
                    />
                  ) : (
                    <Avatar sx={{ width: 48, height: 48, bgcolor: theme.palette.primary.main }} variant="rounded">
                      <Business />
                    </Avatar>
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" color="text.secondary" noWrap>
                      {job.entity?.name || 'Company'}
                    </Typography>
                  </Box>
                  {job.is_remote && (
                    <Chip label="Remote" size="small" color="success" />
                  )}
                </Stack>

                {/* Job Title */}
                <Typography
                  variant="h6"
                  component="h3"
                  fontWeight={600}
                  gutterBottom
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    minHeight: '3.5rem'
                  }}
                >
                  {job.title}
                </Typography>

                {/* Job Details */}
                <Stack spacing={1.5} sx={{ mb: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <LocationOn fontSize="small" sx={{ color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {job.location}
                    </Typography>
                  </Stack>

                  <Stack direction="row" alignItems="center" spacing={1}>
                    
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {formatSalary(job.salary_min, job.salary_max, job.salary_currency)}
                    </Typography>
                  </Stack>

                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CalendarToday fontSize="small" sx={{ color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(job.expiry_date)}
                    </Typography>
                  </Stack>
                </Stack>

                {/* Description */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    flexGrow: 1
                  }}
                >
                  {job.description}
                </Typography>

                {/* Requirements */}
                <Box sx={{ mb: 2 }}>
                  <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                    {job.requirements.slice(0, 3).map((skill, index) => (
                      <Chip
                        key={index}
                        label={skill}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 24 }}
                      />
                    ))}
                    {job.requirements.length > 3 && (
                      <Chip
                        label={`+${job.requirements.length - 3}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem', height: 24 }}
                      />
                    )}
                  </Stack>
                </Box>

                {/* Modern View Button */}
                <Button
                  variant="contained"
                  fullWidth
                  component={Link}
                  to={`/jobs/${job.id}`}
                  startIcon={<Work />}
                  sx={{
                    mt: 'auto',
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 600,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                    }
                  }}
                >
                  View Details
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* No results */}
      {sortedJobs.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            background: alpha(theme.palette.grey[100], 0.5),
            borderRadius: 2
          }}
        >
          <FilterList sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" fontWeight={600} gutterBottom>
            No jobs found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Try adjusting your search criteria or filters
          </Typography>
          <Button
            variant="outlined"
            onClick={() => {
              setSearchTerm('');
              setLocationFilter('');
              setRemoteFilter('all');
              setSortBy('newest');
              setCurrentPage(1);
            }}
          >
            Clear Filters
          </Button>
        </Box>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            size="large"
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </Container>
  );
};
